import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { NERVE_BLOCK_BORDER, PEDIATRIC_BORDER, PEDIATRIC_AGE_MAX } from './staffNameplates.ts';
import { utcToLocalTime } from '../utils/timezone.ts';

// Small popover for hand-marking a single case (operating-room schedule) on the Board: toggle whether it
// needs a Nerve block (yellow border) and/or is Pediatric (hot-pink border). A case can have BOTH. Each
// toggle sets an EXPLICIT override that wins over the automatic detection; "Auto" clears the override.
// Anchored under the case block, portaled to <body>, closes on click-outside / Esc. See caseMarkers.ts.

// Effective state of one flag: its current on/off value, what auto-detection says, and whether a manual
// override is in force (so we can offer "revert to Auto").
export interface FlagState { value: boolean; auto: boolean; overridden: boolean }

interface CaseMarkersPopoverProps {
  anchorRect: DOMRect;
  surgeryType: string;
  startTimeUtc: string;
  nerveBlock: FlagState;
  pediatric: FlagState;
  onToggle: (kind: 'nerveBlock' | 'pediatric', next: boolean) => void; // set explicit override
  onResetAuto: (kind: 'nerveBlock' | 'pediatric') => void;             // clear override
  onClose: () => void;
}

const POPOVER_WIDTH = 244;
const MARGIN = 12;

const CaseMarkersPopover: React.FC<CaseMarkersPopoverProps> = ({
  anchorRect, surgeryType, startTimeUtc, nerveBlock, pediatric, onToggle, onResetAuto, onClose,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Anchor just below the case block, nudged to stay fully on-screen; flip above if there's no room below.
  useEffect(() => {
    let left = anchorRect.left;
    if (left + POPOVER_WIDTH > window.innerWidth - MARGIN) left = Math.max(MARGIN, window.innerWidth - POPOVER_WIDTH - MARGIN);
    let top = anchorRect.bottom + 6;
    const estHeight = 170;
    if (top + estHeight > window.innerHeight - MARGIN) top = Math.max(MARGIN, anchorRect.top - estHeight - 6);
    setPos({ top, left });
  }, [anchorRect]);

  // Close on click-outside or Esc.
  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  const row = (kind: 'nerveBlock' | 'pediatric', color: string, label: string, sublabel: string, st: FlagState) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 4px' }}>
      {/* Border-swatch: a hollow box in this flag's border color, so the swatch reads as "the border you'll get". */}
      <span style={{ width: '15px', height: '15px', borderRadius: '3px', border: `2px solid ${color}`, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0, lineHeight: 1.15 }}>
        <div style={{ fontSize: '0.82em', fontWeight: 700, color: 'var(--text-primary)' }}>{label}</div>
        <div style={{ fontSize: '0.72em', color: 'var(--text-muted)' }}>
          {sublabel}
          {st.overridden && (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => onResetAuto(kind)}
                title={`Clear the manual override and go back to auto (auto: ${st.auto ? 'on' : 'off'})`}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--or-blue-mid)', fontSize: '1em', fontWeight: 600 }}
              >↺ auto ({st.auto ? 'on' : 'off'})</button>
            </>
          )}
        </div>
      </div>
      {/* Toggle switch — reflects the effective value; clicking pins an explicit override to the opposite. */}
      <button
        type="button"
        role="switch"
        aria-checked={st.value}
        aria-label={`${label} ${st.value ? 'on' : 'off'}`}
        onClick={() => onToggle(kind, !st.value)}
        style={{
          position: 'relative', width: '38px', height: '20px', flexShrink: 0, borderRadius: '10px', cursor: 'pointer',
          border: '1px solid ' + (st.value ? color : 'var(--border-soft)'),
          background: st.value ? color : 'var(--bg-input)', transition: 'background 0.12s ease',
        }}
      >
        <span style={{ position: 'absolute', top: '1px', left: st.value ? '19px' : '1px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.35)', transition: 'left 0.12s ease' }} />
      </button>
    </div>
  );

  return ReactDOM.createPortal((
    <div
      ref={ref}
      role="dialog"
      aria-label="Case markers"
      style={{
        position: 'fixed', top: `${pos.top}px`, left: `${pos.left}px`, width: `${POPOVER_WIDTH}px`, zIndex: 100000,
        background: 'var(--popover-bg)', border: '1px solid var(--popover-border)', borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)', padding: '10px 12px', color: 'var(--text-primary)',
      }}
    >
      <div style={{ paddingBottom: '6px', marginBottom: '2px', borderBottom: '1px solid var(--border-subtle)' }}>
        <div style={{ fontSize: '0.85em', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{surgeryType}</div>
        <div style={{ fontSize: '0.7em', color: 'var(--text-muted)' }}>Case start {utcToLocalTime(startTimeUtc)}</div>
      </div>
      {row('nerveBlock', NERVE_BLOCK_BORDER, 'Nerve block', 'Yellow border', nerveBlock)}
      {row('pediatric', PEDIATRIC_BORDER, 'Pediatric', `Hot-pink border · age ≤ ${PEDIATRIC_AGE_MAX}`, pediatric)}
    </div>
  ), document.body);
};

export default CaseMarkersPopover;
